import {
  useAwards,
  useCertifications,
  useEducation,
  useExperience,
  useProfile,
  useProjects,
  useSiteContent,
} from '@personal-portfolio/queries';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router';
import { ResumeCard } from '@/features/resume-download/resume-card';
import { scrollToSection, useScrollSpy } from './use-scroll-spy';
import { QuickContactCard } from './quick-contact-card';

interface RightSidebarProps {
  className?: string;
}

interface NavigationItem {
  title: string;
  id: string;
}

export const RightSidebar: React.FC<RightSidebarProps> = ({
  className = '',
}) => {
  const location = useLocation();
  const content = useSiteContent();
  const { data: profileData } = useProfile();
  const { data: experienceData } = useExperience();
  const { data: projectsData } = useProjects();
  const { data: educationData } = useEducation();
  const { data: certificationsData } = useCertifications();
  const { data: awardsData } = useAwards();

  // Memoized so the derived arrays keep a stable identity between renders;
  // they only change when the route or the underlying data changes.
  const navigationItems = useMemo<NavigationItem[]>(() => {
    switch (location.pathname) {
      case '/experience':
        return (
          experienceData?.map((exp) => ({
            title: exp.company,
            id: exp.slug,
          })) ?? []
        );
      case '/projects':
        return (
          projectsData?.map((project) => ({
            title: project.title,
            id: project.slug,
          })) ?? []
        );
      case '/education':
        return (
          educationData?.map((edu) => ({
            title: edu.institution,
            id: edu.slug,
          })) ?? []
        );
      case '/certifications':
        return (
          certificationsData?.map((cert) => ({
            title: cert.name,
            id: cert.slug,
          })) ?? []
        );
      case '/awards':
        return (
          awardsData?.map((award) => ({
            title: award.title,
            id: award.slug,
          })) ?? []
        );
      default:
        return [];
    }
  }, [
    location.pathname,
    experienceData,
    projectsData,
    educationData,
    certificationsData,
    awardsData,
  ]);

  const sectionIds = useMemo(
    () => navigationItems.map((item) => item.id),
    [navigationItems],
  );
  const activeId = useScrollSpy(sectionIds);

  // The projects page renders cards in two columns, so both items of the
  // active row are highlighted together.
  const activeIds = useMemo(() => {
    if (!activeId) return [];
    if (location.pathname !== '/projects') return [activeId];
    const index = sectionIds.indexOf(activeId);
    if (index === -1) return [activeId];
    const rowStart = index - (index % 2);
    return sectionIds.slice(rowStart, rowStart + 2);
  }, [activeId, location.pathname, sectionIds]);

  return (
    <aside
      aria-label="Page navigation"
      className={`w-80 space-y-8 border-l bg-transparent p-6 ${className}`}
    >
      {navigationItems.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-mono text-xs font-medium tracking-widest text-muted-foreground uppercase">
            {content.chrome.onThisPage}
          </h3>
          <nav className="flex flex-col border-l border-border">
            {navigationItems.map((item) => {
              const isActive = activeIds.includes(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  aria-current={isActive ? 'location' : undefined}
                  className={`-ml-px w-full cursor-pointer border-l-2 py-1 pl-3 text-left text-xs transition-colors duration-150 ${
                    isActive
                      ? 'border-foreground font-medium text-foreground'
                      : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
                  }`}
                >
                  {item.title}
                </button>
              );
            })}
          </nav>
        </div>
      )}

      <ResumeCard />

      <QuickContactCard email={profileData?.email} phone={profileData?.phone} />
    </aside>
  );
};
